import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Check, Copy, MailCheck } from 'lucide-react';

import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { buildStatsRange } from '@modules/_shared/components/document-stats/OpensBarChart';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useCopiedTooltip } from '@modules/_shared/hooks/useCopiedTooltip';
import { useDocumentUrl } from '@modules/_shared/hooks/useDocumentUrl';
import { copyPromiseToClipboard } from '@modules/_shared/utils/clipboard';
import { useLocale } from '@shared/hooks/locale';
import type { Language } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

import {
  GET_BROCHURE_RECIPIENT_STATS,
  TRACKED_BROCHURE_LINK,
} from './brochureRecipientsQueries';
import { ListingBuyerSelect } from './ListingBuyerSelect';

const STATS_RANGE_DAYS = 90;

type ViewStatusProps = {
  documentId: string;
  recipientId: string;
  createdUrl: string | null;
};

const ViewStatus = ({
  documentId,
  recipientId,
  createdUrl,
}: ViewStatusProps) => {
  const { t } = useLocale();
  const [currentError, setCurrentError] = useState<ErrorLike | null>(null);

  const [range, setRange] = useState(() => buildStatsRange(STATS_RANGE_DAYS));

  const { data, loading, error } = useQuery(GET_BROCHURE_RECIPIENT_STATS, {
    variables: {
      documentId,
      recipientId,
      from: range.from,
      to: range.to,
    },
    fetchPolicy: 'cache-and-network',
  });

  const totalOpens = data?.document_stats?.total_opens ?? 0;

  useEffect(() => {
    if (error != null) {
      setCurrentError(error);
    }
  }, [error]);

  if (loading && data?.document_stats == null) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {t('Checking views…')}
        </Typography>
      </Stack>
    );
  }

  if (error != null) {
    return (
      <>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setRange(buildStatsRange(STATS_RANGE_DAYS));
              }}
            >
              {t('Retry')}
            </Button>
          }
        >
          {t('View status is temporarily unavailable')}
        </Alert>
        <MutationErrorSnackbar
          error={currentError}
          onClose={() => setCurrentError(null)}
        />
      </>
    );
  }

  if (totalOpens === 0 && createdUrl == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('Not viewed yet')}
      </Typography>
    );
  }

  if (totalOpens === 0) {
    return (
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Check size={18} color="var(--mui-palette-success-main)" />
          <Typography variant="body2">{t('Link created')}</Typography>
        </Stack>
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ maxWidth: '100%' }}
        >
          {createdUrl}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <MailCheck size={18} color="var(--mui-palette-success-main)" />
      <Typography variant="body2">
        {t('Viewed {{count}} time', { count: totalOpens })}
      </Typography>
    </Stack>
  );
};

type BuyerLinkDialogProps = {
  lotId: string;
  templateId: string;
  language: Language;
  onClose: () => void;
  onLinkCreated: () => void;
};

export const BuyerLinkDialog = ({
  lotId,
  templateId,
  language,
  onClose,
  onLinkCreated,
}: BuyerLinkDialogProps) => {
  const { t } = useLocale();

  const [userId, setUserId] = useState<string | null>(null);
  const [trackedUrl, setTrackedUrl] = useState<string | null>(null);
  const [buyerCount, setBuyerCount] = useState<number | null>(null);

  const [createLinkError, setCreateLinkError] = useState<ErrorLike | null>(
    null,
  );
  const [createLink, { loading: buyerLinkLoading }] = useMutation(
    TRACKED_BROCHURE_LINK,
  );
  const {
    showCopiedTooltip: showBuyerCopiedTooltip,
    onCopied: onBuyerCopied,
    resetCopied: resetBuyerCopied,
  } = useCopiedTooltip();

  const recipientId = userId != null ? toGlobalId('User', userId) : null;

  const recipientIdRef = useRef(recipientId);
  useEffect(() => {
    recipientIdRef.current = recipientId;
  }, [recipientId]);

  const documentId = useMemo(() => toGlobalId('Lot', lotId), [lotId]);
  const documentUrl = useDocumentUrl(templateId, documentId, language);

  // Mint the tracked link as soon as a buyer is picked, so selecting the buyer
  // is the "create link" action and the button below is a pure copy affordance.
  const mintLink = useCallback(
    async (recId: string) => {
      const res = await createLink({
        variables: {
          url: documentUrl.href,
          documentType: Doc_Template_Type_Enum_Enum.LotBrochure,
          recipientId: recId,
        },
        onError: error => {
          if (recipientIdRef.current === recId) {
            setCreateLinkError(error);
          }
        },
      });
      // Drop the result if the user switched buyers while the mint was in
      // flight, otherwise we'd store this link under the newly-selected buyer.
      if (recipientIdRef.current !== recId) {
        return;
      }
      setTrackedUrl(res.data?.tracked_document_url?.url ?? null);
      onLinkCreated();
    },
    [createLink, documentUrl, onLinkCreated],
  );

  const handleCopy = useCallback(() => {
    if (trackedUrl == null) {
      return;
    }
    const showCopyError = (error: unknown) => {
      setCreateLinkError(
        error instanceof Error ? error : new Error(t('somethingWentWrong')),
      );
    };
    copyPromiseToClipboard(Promise.resolve(trackedUrl), {
      onCopied: onBuyerCopied,
      onError: showCopyError,
    });
  }, [trackedUrl, onBuyerCopied, t]);

  const onUserChange = useCallback(
    (nextUserId: string | null) => {
      const recId = nextUserId != null ? toGlobalId('User', nextUserId) : null;
      // Keep selection and deselection current before the ref effect runs.
      recipientIdRef.current = recId;
      setUserId(nextUserId);
      setTrackedUrl(null);
      setCreateLinkError(null);
      resetBuyerCopied();
      if (recId != null) {
        mintLink(recId).catch(() => {});
      }
    },
    [mintLink, resetBuyerCopied],
  );

  let buyerLinkButtonIcon = <Copy size={16} />;
  if (buyerLinkLoading) {
    buyerLinkButtonIcon = <CircularProgress size={16} />;
  } else if (showBuyerCopiedTooltip) {
    buyerLinkButtonIcon = <Check size={16} />;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('Link for a specific buyer')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'Create a personal brochure link for a potential buyer, so that you can see when and how many times they opened the listing brochure.',
            )}
          </Typography>

          {buyerCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t(
                'No buyers linked to this listing yet. Add buyers from the Buyers tab.',
              )}
            </Typography>
          ) : (
            <ListingBuyerSelect
              lotId={lotId}
              userId={userId}
              onChange={onUserChange}
              onBuyersCountChange={setBuyerCount}
              placeholder={t('Search buyers')}
            />
          )}

          {recipientId != null && (
            <ViewStatus
              key={`${documentId}:${recipientId}`}
              documentId={documentId}
              recipientId={recipientId}
              createdUrl={trackedUrl}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
        {buyerCount !== 0 && (
          <Button
            variant="contained"
            disabled={trackedUrl == null || buyerLinkLoading}
            onClick={handleCopy}
            startIcon={buyerLinkButtonIcon}
          >
            {showBuyerCopiedTooltip ? t('Copied') : t('Copy link')}
          </Button>
        )}
      </DialogActions>

      {showBuyerCopiedTooltip && (
        <RASnackbar
          message={t('Link copied to clipboard')}
          onClose={resetBuyerCopied}
          severity="success"
        />
      )}
      <MutationErrorSnackbar
        error={createLinkError}
        onClose={() => setCreateLinkError(null)}
      />
    </Dialog>
  );
};
